// StatsOverview.tsx
import React, { useEffect, useState } from "react";
import { DashboardData, DateRangeSummary } from "./types/dashboard";
import { Wallet, Package, AlertCircle, BarChart3, ChevronRight, Box, Gift, Truck, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUser } from "src/shared/context/UserContext";
import { useFormatAmount } from "../home/hooks/useFormatAmount";
import { RangePreset } from "./utils/dateRange";
import DateFilterBar from "./DateFilterBar";

interface StatsOverviewProps {
  dashboardData: DashboardData;
  preset: RangePreset;
  startDate: string;
  endDate: string;
  onRangeChange: (startDate: string, endDate: string, preset: RangePreset) => void;
}

const StatsOverview: React.FC<StatsOverviewProps> = ({ dashboardData, preset, startDate, endDate, onRangeChange }) => {
  type BusinessType = "Laundry" | "Restaurant" | "Saloon" | "Car Wash" | "Grocery";
  const [businessType, setBusinessType] = useState<BusinessType | "">("");
  const navigate = useNavigate();
  const { user } = useUser();
  const formatAmount = useFormatAmount();

  // Extract array safely from data range summary
  const summary: DateRangeSummary[] = Array.isArray(dashboardData?.date_range_res) ? dashboardData.date_range_res : dashboardData?.date_range_res ? [dashboardData.date_range_res] : [];

  const sum = (key: keyof DateRangeSummary | string): number => summary.reduce((acc, row) => acc + (Number(row?.[key as keyof DateRangeSummary]) || 0), 0);

  // Corrected function calls
  const totalRevenueAmount = sum("total_received") + sum("ledger_received") - sum("total_cancel_refund") - sum("ledger_refund");
  const totalRevenue = formatAmount(totalRevenueAmount);
  const activeOrders = sum("range_count") || sum("order_count");
  const totalSales = formatAmount(sum("total_sales"));
  const totaltips = formatAmount(sum("total_tip"));
  const totalParcelFee = formatAmount(sum("total_parcel_fee"));
  const totalDiscount = formatAmount(sum("total_discount") + sum("ledger_discount"));
  const totalDeliveryFee = formatAmount(sum("total_delivery_fee"));
  const outstanding = formatAmount(dashboardData?.overall_outstanding_balance || 0);

  useEffect(() => {
    const storedType = sessionStorage.getItem("business_type") as BusinessType | null;
    if (storedType) setBusinessType(storedType);
  }, []);

  const isRestaurant = businessType === "Restaurant";

  const baseStats = [
    {
      title: "Total Received",
      value: `QAR ${totalRevenue.toLocaleString()}`,
      icon: <Wallet className="w-5 h-5 text-emerald-600" />,
      gradient: "from-emerald-50 to-white",
      borderColor: "border-emerald-500",
    },
    // {
    //   title: "Ledger Received",
    //   value: totalledgerReceived,
    //   icon: <Wallet className="w-5 h-5 text-cyan-600" />,
    //   gradient: "from-cyan-50 to-white",
    //   borderColor: "border-cyan-500",
    // },
    {
      title: "Orders",
      value: activeOrders.toString(),
      icon: <Package className="w-5 h-5 text-blue-600" />,
      gradient: "from-blue-50 to-white",
      borderColor: "border-blue-500",
    },
    {
      title: "Total Discount",
      value: `QAR ${totalDiscount.toLocaleString()}`,
      icon: <Tag className="w-5 h-5 text-purple-600" />,
      gradient: "from-purple-50 to-white",
      borderColor: "border-purple-500",
    },
    {
      title: "Total Delivery Fee",
      value: `QAR ${totalDeliveryFee.toLocaleString()}`,
      icon: <Truck className="w-5 h-5 text-green-600" />,
      gradient: "from-green-50 to-white",
      borderColor: "border-green-500",
    },
  ];

  const restaurantStats = [
    {
      title: "Total Tips",
      value: `QAR ${totaltips.toLocaleString()}`,
      icon: <Gift className="w-5 h-5 text-pink-600" />,
      gradient: "from-pink-50 to-white",
      borderColor: "border-pink-500",
    },
    {
      title: "Total Parcel Fee",
      value: `QAR ${totalParcelFee.toLocaleString()}`,
      icon: <Box className="w-5 h-5 text-indigo-600" />,
      gradient: "from-indigo-50 to-white",
      borderColor: "border-indigo-500",
    },
  ];

  const totalSalesStat = {
    title: "Total Sales",
    value: `QAR ${totalSales.toLocaleString()}`,
    icon: <BarChart3 className="w-5 h-5 text-amber-600" />,
    gradient: "from-amber-50 to-white",
    borderColor: "border-amber-500",
  };

  const mainStats = [...baseStats, ...(isRestaurant ? restaurantStats : []), totalSalesStat];

  const getGridCols = () => {
    const count = mainStats.length;
    if (count >= 7) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
    if (count >= 5) return "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3";
    if (count >= 3) return "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3";
    return "grid-cols-1 sm:grid-cols-2";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className={`bg-white rounded-2xl shadow-md p-4 ${!isRestaurant ? "lg:col-span-3" : "lg:col-span-4"}`}>
        {/* Date Filter Bar styled seamlessly into the card header */}
        <DateFilterBar
          title="Overview"
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onRangeChange={onRangeChange}
          size="compact"
          className="!bg-transparent !p-0 !border-none !shadow-none !rounded-none !mb-4"
        />

        {/* Stats Grid */}
        <div className={`grid gap-4 ${getGridCols()}`}>
          {mainStats.map((stat, i) => (
            <div key={i} className={`relative bg-gray-200 rounded-xl border-l-4 ${stat.borderColor} shadow-md transition-shadow duration-200`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-50 rounded-xl`} />
              <div className="relative p-4 flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-medium">{stat.title}</p>
                  <p className={`text-lg font-semibold break-words ${stat.title === "Total Received" && totalRevenueAmount < 0 ? "text-red-600" : "text-gray-900"}`}>{stat.value}</p>
                </div>
                <div className="ml-3 flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isRestaurant && (
        <div className="bg-white rounded-2xl shadow-md p-4 lg:col-span-1 h-full flex flex-col">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Overall Outstanding</h2>
          <div className="relative bg-white rounded-xl border-l-4 border-red-500 flex-1 flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-white opacity-50 rounded-xl" />
            <div className="relative p-4 flex-1 flex flex-col">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1 break-words">QAR {outstanding.toLocaleString()}</p>
                </div>
                <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>

              {user?.businessType !== "Grocery" && (
                <div
                  className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between cursor-pointer group"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/v1/ledger");
                  }}
                >
                  <span className="text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors">View Ledger</span>
                  <ChevronRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsOverview;
